/**
 * Reconcile a cart against the outcome of a split checkout group.
 *
 * A committed sale is one whose saleStatus is 'invoiced' or 'completed' —
 * the backend considers those finalized and stock/payment have been consumed.
 *
 * For each committed sale, the quantities of its items are subtracted from
 * the cart. Items whose remaining quantity reaches zero are removed entirely.
 * Items from failed/pending/draft sales stay in the cart untouched.
 *
 * Apply only after every child has a terminal outcome. The caller records
 * the completed group so recovery cannot subtract its quantities twice.
 */

const COMMITTED_STATUSES = new Set(['invoiced', 'completed']);
const FAILED_STATUSES = new Set(['cancelled', 'refunded', 'rejected']);

// Placement/payment responses use saleStatus; GET checkout-groups uses status.
export const saleStatusOf = (sale) => sale.saleStatus ?? sale.status;
export const isCommittedSale = (sale) => COMMITTED_STATUSES.has(saleStatusOf(sale));

export const splitGroupOutcome = (group) => {
  const sales = group?.sales;
  if (!Array.isArray(sales) || sales.length === 0 || group.status === 'creating') return 'pending';
  if (sales.every(sale => isCommittedSale(sale) || FAILED_STATUSES.has(saleStatusOf(sale)))) return 'settled';
  // Resumability permits paying, never proves that an order was completed.
  if (group.canResumePayment === true && sales.every(sale => saleStatusOf(sale) === 'draft')) return 'ready';
  // COD/wallet have no gateway payment to resume, but an interrupted fanout
  // still needs POST resume-checkout to finish the existing child orders.
  if (['cod', 'wallet'].includes(group.paymentMethod) && group.status === 'processing'
    && sales.some(sale => saleStatusOf(sale) === 'draft')) return 'ready';
  return 'pending';
};

/**
 * Determine which product quantities were committed across all sales.
 *
 * @param {Array} sales - Array of sale objects with { saleStatus, items: [{ productId, quantity }] }
 * @returns {Map<string, number>} productId → total committed quantity
 */
export const committedQuantities = (sales) => {
  const committed = new Map();
  for (const sale of sales) {
    if (!isCommittedSale(sale)) continue;
    for (const item of sale.items || []) {
      const pid = String(item.productId);
      committed.set(pid, (committed.get(pid) || 0) + Number(item.quantity || 0));
    }
  }
  return committed;
};

/**
 * Compute what should remain in the cart after reconciliation.
 *
 * @param {Array} cartItems - Current cart: [{ productId, quantity, product }]
 * @param {Array} sales - Group sales with items and saleStatus
 * @returns {{ remaining: Array<{ productId: string, quantity: number }>, removed: Array<{ productId: string, quantity: number }> }}
 */
export const reconcileCart = (cartItems, sales) => {
  const committed = committedQuantities(sales);
  const remaining = [];
  const removed = [];

  for (const cartItem of cartItems) {
    const pid = String(cartItem.productId);
    const cartQty = Number(cartItem.quantity || 0);
    const committedQty = committed.get(pid) || 0;
    const remainQty = Math.max(0, cartQty - committedQty);

    if (remainQty > 0) {
      remaining.push({ productId: pid, quantity: remainQty });
    }
    if (committedQty > 0) {
      removed.push({ productId: pid, quantity: Math.min(cartQty, committedQty) });
    }
  }

  return { remaining, removed };
};

/**
 * Whether ALL sales in the group are committed (full success).
 */
export const isFullSuccess = (sales) =>
  sales.length > 0 && sales.every(isCommittedSale);

/**
 * Whether the group has at least one committed and at least one non-committed sale.
 */
export const isPartialSuccess = (sales) => {
  const hasCommitted = sales.some(isCommittedSale);
  const hasUncommitted = sales.some((s) => !isCommittedSale(s));
  return hasCommitted && hasUncommitted;
};

/**
 * Whether NO sale in the group is committed (all failed/pending).
 */
export const isAllFailed = (sales) =>
  sales.length > 0 && sales.every((s) => !isCommittedSale(s));

const RECONCILED_KEY = 'fnh_reconciled_groups';

const readReconciledGroups = () => {
  try {
    const raw = localStorage.getItem(RECONCILED_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch { return {}; }
};

const writeReconciledGroups = (groups) => {
  try { localStorage.setItem(RECONCILED_KEY, JSON.stringify(groups)); } catch {}
};

export const markGroupReconciled = (groupId) => {
  const groups = readReconciledGroups();
  groups[groupId] = Date.now();
  writeReconciledGroups(groups);
};

export const isGroupReconciled = (groupId) => {
  return Boolean(readReconciledGroups()[groupId]);
};
